import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const WebSocketContext = createContext();

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  // Pending message queue for messages that failed to send while disconnected
  const pendingMessagesRef = useRef([]);
  // Flag to suppress auto-resume when we have a pending queue to flush first
  const suppressResumeRef = useRef(false);

  // Generate unique session ID when component mounts
  useEffect(() => {
    const generateSessionId = () => {
      return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    };
    setSessionId(generateSessionId());
  }, []);

  // Flush any pending messages after reconnection and authentication
  const flushPendingMessages = useCallback((ws) => {
    if (pendingMessagesRef.current.length > 0) {
      console.log(`Flushing ${pendingMessagesRef.current.length} pending messages after reconnection...`);
      const messages = [...pendingMessagesRef.current];
      pendingMessagesRef.current = [];
      
      for (const msg of messages) {
        try {
          ws.send(JSON.stringify(msg));
          console.log(`Flushed pending message: ${msg.type}`);
        } catch (err) {
          console.error('Error flushing pending message:', err);
          // Re-queue failed messages
          pendingMessagesRef.current.push(msg);
        }
      }
      
      // After flushing, clear the suppress flag
      suppressResumeRef.current = false;
    }
  }, []);

  const connect = useCallback(() => {
    if (!userId) return;

    const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:3001';
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected for user:', userId);
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        
        // If we have pending messages, set the suppress flag so we don't
        // blindly accept a stale GAME_RESUME that would overwrite local state
        if (pendingMessagesRef.current.length > 0) {
          suppressResumeRef.current = true;
          console.log(`Reconnection with ${pendingMessagesRef.current.length} pending messages — will suppress auto-resume`);
        }
        
        // Authenticate with the server including session ID
        ws.send(JSON.stringify({
          type: 'AUTHENTICATE',
          userId: userId,
          sessionId: sessionId
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle authentication response
          if (data.type === 'AUTHENTICATED') {
            console.log('WebSocket authenticated for user:', data.userId);
            
            // If we have pending messages, flush them first instead of requesting resume
            if (pendingMessagesRef.current.length > 0) {
              console.log('Have pending messages — flushing instead of requesting game resume');
              flushPendingMessages(ws);
              // After flushing, request a fresh game resume so server state is current
              console.log('Requesting game resume after flushing pending messages...');
              ws.send(JSON.stringify({
                type: 'REQUEST_GAME_RESUME'
              }));
            } else {
              // No pending messages, normal flow — request game resumption
              console.log('Requesting game resume after authentication...');
              ws.send(JSON.stringify({
                type: 'REQUEST_GAME_RESUME'
              }));
            }
          }
          
          // Dispatch custom event for other components to listen to
          window.dispatchEvent(new CustomEvent('websocket-message', {
            detail: data
          }));
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        setSocket(null);
        
        // Only attempt to reconnect if it wasn't a manual close and we haven't exceeded attempts
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          console.log(`Attempting to reconnect... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setError('Failed to reconnect to server after multiple attempts');
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('WebSocket connection error');
      };

      setSocket(ws);
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setError('Failed to create WebSocket connection');
    }
  }, [userId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (socket) {
      socket.close(1000, 'Manual disconnect');
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket]);

  const sendMessage = useCallback((message) => {
    // List of message types that should be queued when offline
    const queueableTypes = [
      'SAVE_GAME_STATE', 'BROADCAST_MOVE', 'BROADCAST_ACTION',
      'BROADCAST_TEAM_MOVE', 'BROADCAST_TEAM_ACTION', 'LEVEL_COMPLETED'
    ];
    
    if (socket && isConnected) {
      try {
        console.log('Sending WebSocket message:', message.type);
        socket.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        // Queue the message for retry on reconnection
        if (queueableTypes.includes(message.type)) {
          // For SAVE_GAME_STATE, replace any existing queued save for the same level
          // to avoid sending stale intermediate states on reconnect
          if (message.type === 'SAVE_GAME_STATE') {
            pendingMessagesRef.current = pendingMessagesRef.current.filter(
              msg => !(msg.type === 'SAVE_GAME_STATE' && msg.level === message.level)
            );
          }
          console.log(`Queuing failed message for retry: ${message.type}`);
          pendingMessagesRef.current.push(message);
        }
        return false;
      }
    }
    // Queue important messages for when we reconnect
    if (queueableTypes.includes(message.type)) {
      // For SAVE_GAME_STATE, keep only the latest save per level to avoid queue bloat
      if (message.type === 'SAVE_GAME_STATE') {
        pendingMessagesRef.current = pendingMessagesRef.current.filter(
          msg => !(msg.type === 'SAVE_GAME_STATE' && msg.level === message.level)
        );
      }
      // For BROADCAST_MOVE/ACTION, also deduplicate — only the latest state matters
      if (message.type === 'BROADCAST_MOVE' || message.type === 'BROADCAST_ACTION') {
        pendingMessagesRef.current = pendingMessagesRef.current.filter(
          msg => !(msg.type === message.type && msg.level === message.level)
        );
      }
      console.log(`WebSocket not connected, queuing message for later: ${message.type} (queue size: ${pendingMessagesRef.current.length + 1})`);
      pendingMessagesRef.current.push(message);
    } else {
      console.log('WebSocket not connected, cannot send message:', message.type);
    }
    return false;
  }, [socket, isConnected]);

  const setUser = useCallback((newUserId) => {
    if (newUserId !== userId) {
      // Disconnect existing connection if user changes or logs out
      if (socket) {
        console.log('User changed/logged out, disconnecting WebSocket');
        disconnect();
      }
      
      // Clear reconnection attempts and pending queue when user logs out
      if (newUserId === null) {
        reconnectAttemptsRef.current = 0;
        pendingMessagesRef.current = [];
        suppressResumeRef.current = false;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        setError(null);
        console.log('User logged out, WebSocket state reset');
      }
      
      setUserId(newUserId);
    }
  }, [userId, socket, disconnect]);

  // Connect when userId is available
  useEffect(() => {
    if (userId && !socket) {
      connect();
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (socket) {
        socket.close(1000, 'Component unmounting');
      }
    };
  }, [userId, socket, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    socket,
    isConnected,
    error,
    sendMessage,
    connect,
    disconnect,
    setUser,
    sessionId,
    suppressResumeRef,
    pendingMessagesRef
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
